import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { parseAsJson, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import * as yup from 'yup';
import { useInsurancePolicySummaryQuery } from '../../../../queries/InsurancePolicy/useInsurancePolicySummary';
import { usePoliciesReportingChartsQuery } from '../../../../queries/Policies/usePoliciesReportingCharts';
import { Page } from '../../../components-v2/PageLayout';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { ROUTES } from '../../../constants/routes';
import { baseStyleSheet } from '../../../styles/styles';
import { DataDisplay } from '../components/DataDisplay';
import { formatCurrency } from '../helpers/formatCurrency';
import { styleSheet as advancedReportingStyleSheet } from '../styles';
import { CommissionsByOwnerTreemapChart } from './components/CommissionsByOwnerChart';
import { PoliciesByLineOfBusinessChart } from './components/PoliciesByLineOfBusinessChart';
import { PoliciesFilters } from './components/PoliciesFilters';
import { PremiumAndAverageByCarrierChart } from './components/PremiumAndAverageByCarrierChart';
import { PremiumDistributionChart } from './components/PremiumDistributionChart';
import { PremiumOverTimeChart } from './components/PremiumOverTimeChart';

const charts: Api.IInsurancePolicyChartConfiguration[] = [
	{
		key: 'premiumOverTime',
		title: 'Premium Over Time',
		groupBy: 'Date',
		aggregation: 'Sum',
		aggregateField: 'premium',
		dateGranularity: 'Month',
		sortField: 'Label',
		sortAscending: true,
		visualizationType: 'Line',
	},
	{
		key: 'premiumByCarrier',
		title: 'Premium by Carrier',
		groupBy: 'Carrier',
		aggregation: 'Sum',
		aggregateField: 'premium',
		limit: 10,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Pie',
	},
	{
		key: 'policiesByLineOfBusiness',
		title: 'Policies by Line of Business',
		groupBy: 'LineOfBusiness',
		aggregation: 'Count',
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Bar',
	},
	{
		key: 'premiumDistribution',
		title: 'Premium Distribution',
		groupBy: 'PremiumRange',
		aggregation: 'Count',
		visualizationType: 'Bar',
	},
	{
		key: 'commissionsByOwner',
		title: 'Commissions by Policy Owner',
		groupBy: 'Owner',
		aggregation: 'Sum',
		aggregateField: 'commissions',
		limit: 5,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Bar',
	},
	{
		key: 'averagePremiumByCarrier',
		title: 'Average Premium by Carrier',
		groupBy: 'Carrier',
		aggregation: 'Average',
		aggregateField: 'premium',
		limit: 10,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Bar',
	},
];

const createCriteriaSchema = (): yup.AnySchema => {
	return yup.array().of(
		yup.object({
			property: yup.string().optional(),
			op: yup.string().optional(),
			value: yup.string().optional(),
		})
	);
};

const criteriaSchema = createCriteriaSchema();

const validateFilterCriteria = (value: unknown): Api.IInsurancePolicyFilterCriteria[] | null => {
	try {
		const validated = criteriaSchema.validateSync(value, { strict: false, abortEarly: false });
		return validated as unknown as Api.IInsurancePolicyFilterCriteria[];
	} catch {
		return null;
	}
};

const normalizeCriteria = (criteria: Api.IInsurancePolicyFilterCriteria[]): Api.IInsurancePolicyFilterCriteria[] => {
	return criteria.map(c => {
		const normalized: Api.IInsurancePolicyFilterCriteria = {};
		if (c.property) normalized.property = c.property;
		if (c.op) normalized.op = c.op;
		if (c.value) normalized.value = c.value;
		if (c.criteria && c.criteria.length > 0) {
			normalized.criteria = normalizeCriteria(c.criteria);
		}
		return normalized;
	});
};

export const PoliciesAdvancedReporting = () => {
	const [filterCriteria, setFilterCriteria] = useQueryState(
		'filterCriteria',
		parseAsJson<Api.IInsurancePolicyFilterCriteria[]>(validateFilterCriteria).withDefault([])
	);

	const normalizedCriteria = useMemo(() => normalizeCriteria(filterCriteria || []), [filterCriteria]);
	const isUpdatingRef = React.useRef(false);
	const previousCriteriaStringRef = React.useRef<string>('');

	const policiesReportingChartsQuery = usePoliciesReportingChartsQuery({
		enabled: true,
		filterRequest: {
			criteria: normalizedCriteria,
		} as Api.IInsurancePolicyFilterRequest,
		charts,
	});

	const chartsData = policiesReportingChartsQuery.data;

	const handleFilterChange = React.useCallback(
		(newCriteria: Api.IInsurancePolicyFilterCriteria[]) => {
			if (isUpdatingRef.current) return;

			const normalized = normalizeCriteria(newCriteria);
			const criteriaString = JSON.stringify(normalized);

			if (criteriaString !== previousCriteriaStringRef.current) {
				isUpdatingRef.current = true;
				previousCriteriaStringRef.current = criteriaString;
				setFilterCriteria(normalized.length > 0 ? normalized : null);

				// Reset the flag after a short delay to allow URL update to complete
				setTimeout(() => {
					isUpdatingRef.current = false;
				}, 0);
			}
		},
		[setFilterCriteria]
	);

	React.useEffect(() => {
		const criteriaString = JSON.stringify(normalizedCriteria);
		if (criteriaString !== previousCriteriaStringRef.current && !isUpdatingRef.current) {
			previousCriteriaStringRef.current = criteriaString;
		}
	}, [normalizedCriteria]);

	const policySummaryQuery = useInsurancePolicySummaryQuery({
		enabled: true,
		filterRequest: {
			criteria: normalizedCriteria,
		} as Api.IInsurancePolicyFilterRequest,
	});

	const averagePremiumPerPolicy = useMemo(() => {
		if (!policySummaryQuery?.data || policySummaryQuery?.data?.totalPolicies === 0) {
			return 0;
		}

		return formatCurrency(policySummaryQuery?.data?.totalAmount / policySummaryQuery?.data?.totalPolicies);
	}, [policySummaryQuery?.data]);

	return (
		<Page>
			<div className={css(advancedReportingStyleSheet.pageContainer)}>
				<MultiContainerHeader
					appBarHeader={
						<Breadcrumb>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to={ROUTES.dataBoards.home}>
											<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Data Boards</h1>
										</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to={ROUTES.dataBoards.policies}>
											<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Policies</h1>
										</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbPage>Advanced Reporting</BreadcrumbPage>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
					}
				/>

				<div className={css(advancedReportingStyleSheet.filterSection)}>
					<div className={css(advancedReportingStyleSheet.filterHeader)}>
						<PoliciesFilters filterCriteria={normalizedCriteria} onFilterChange={handleFilterChange} />
					</div>
				</div>

				<div id='exportable-content'>
					<div className={css(advancedReportingStyleSheet.container)}>
						<DataDisplay
							title='Total Policies'
							data={policySummaryQuery?.data?.totalPolicies ?? 0}
							loading={policySummaryQuery?.isLoading}
						/>
						<DataDisplay
							title='Total Premium'
							data={formatCurrency(policySummaryQuery?.data?.totalAmount ?? 0)}
							loading={policySummaryQuery?.isLoading}
						/>
						<DataDisplay
							title='Average Premium Per Policy'
							data={averagePremiumPerPolicy}
							loading={policySummaryQuery?.isLoading}
							className={css(advancedReportingStyleSheet.bigDataDisplay)}
						/>
					</div>

					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						<PremiumOverTimeChart
							data={chartsData?.premiumOverTime}
							isLoading={policiesReportingChartsQuery.isLoading}
						/>
						<CommissionsByOwnerTreemapChart
							data={chartsData?.commissionsByOwner}
							isLoading={policiesReportingChartsQuery.isLoading}
						/>
					</div>

					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						<PoliciesByLineOfBusinessChart
							data={chartsData?.policiesByLineOfBusiness}
							isLoading={policiesReportingChartsQuery.isLoading}
						/>
						<PremiumDistributionChart
							data={chartsData?.premiumDistribution}
							isLoading={policiesReportingChartsQuery.isLoading}
						/>
					</div>

					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						<PremiumAndAverageByCarrierChart
							premiumByCarrier={chartsData?.premiumByCarrier}
							averagePremiumByCarrier={chartsData?.averagePremiumByCarrier}
							isLoading={policiesReportingChartsQuery.isLoading}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};
